// A deliberately small JSONPath evaluator, plus the schema-skeleton builder it runs against.
//
// The loader's coherence check builds a skeleton instance of `output_schema` and requires every
// score jsonpath to match EXACTLY ONE node and every extra_columns path to match AT LEAST ONE.
// Running that check client-side is what turns "your path is wrong" from a lock-time discovery
// into a while-you-type one.
//
// We support only the dotted / bracket-quoted / array-index subset — `$.A.B`, `$['A B'].C`,
// `$.A[0].B` — which is every form the shipped specs actually use. Filters, wildcards, slices and
// script expressions return `{ supported: false }` and the UI ABSTAINS rather than asserting a
// verdict it cannot back. No jsonpath dependency: guessing at jsonpath_ng.ext's semantics with a
// different library would be a plausible-looking lie.

const SEGMENT = /^(?:\.([A-Za-z_$][\w$]*)|\['([^']*)'\]|\["([^"]*)"\]|\[(\d+)\])/;

// Parses `$.A['B c'][0]` -> ['A', 'B c', '0'].  Returns null for anything outside the subset.
export const parseJsonPath = (expr) => {
  const raw = String(expr == null ? '' : expr).trim();
  if (!raw.startsWith('$')) return null;

  let rest = raw.slice(1);
  const segments = [];

  while (rest.length > 0) {
    const match = SEGMENT.exec(rest);
    if (!match) return null;
    const value =
      match[1] !== undefined
        ? match[1]
        : match[2] !== undefined
        ? match[2]
        : match[3] !== undefined
        ? match[3]
        : match[4];
    segments.push(value);
    rest = rest.slice(match[0].length);
  }

  return segments;
};

// Builds an example instance of a strict-mode output_schema. Objects become objects with every
// declared property, arrays become a one-element array, scalars become a typed placeholder.
export const buildSkeleton = (schema) => {
  if (!schema || typeof schema !== 'object') return null;

  // An `enum` node resolves to its FIRST member, which is what the loader's skeleton uses. It
  // changes no match count — an enum node is a leaf either way — but a skeleton that disagreed with
  // the engine's on a value would be a lie waiting for a rule that reads one.
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

  const type = Array.isArray(schema.type) ? schema.type.find((t) => t !== 'null') || schema.type[0] : schema.type;

  if (type === 'object') {
    const out = {};
    const properties = schema.properties && typeof schema.properties === 'object' ? schema.properties : {};
    Object.keys(properties).forEach((key) => {
      out[key] = buildSkeleton(properties[key]);
    });
    return out;
  }
  if (type === 'array') return [buildSkeleton(schema.items)];
  if (type === 'string') return '';
  if (type === 'number' || type === 'integer') return 0;
  if (type === 'boolean') return false;
  return null;
};

// Resolves a parsed path against an instance. Returns every matched node — the subset we support
// is deterministic, so this is 0 or 1 nodes in practice, but the count is what the caller checks.
const resolve = (instance, segments) => {
  let cursor = instance;
  for (let i = 0; i < segments.length; i += 1) {
    if (cursor === null || typeof cursor !== 'object') return [];
    const segment = segments[i];
    if (Array.isArray(cursor)) {
      const idx = Number(segment);
      if (!Number.isInteger(idx) || idx < 0 || idx >= cursor.length) return [];
      cursor = cursor[idx];
    } else {
      if (!Object.prototype.hasOwnProperty.call(cursor, segment)) return [];
      cursor = cursor[segment];
    }
  }
  return [cursor];
};

// The one entry point the UI uses.
//   { supported: false }                        -> outside our subset; abstain, say so
//   { supported: true, matches: <n> }           -> resolved against the schema skeleton
export const matchCount = (expr, schema) => {
  const segments = parseJsonPath(expr);
  if (segments === null) return { supported: false, matches: null };
  if (!schema) return { supported: false, matches: null };
  const skeleton = buildSkeleton(schema);
  if (skeleton === null || typeof skeleton !== 'object') return { supported: false, matches: null };
  return { supported: true, matches: resolve(skeleton, segments).length };
};

// Every leaf path of a skeleton, rendered back as a JSONPath expression. Powers pick-from-shape,
// which is what makes the coherence check free rather than asserted.
export const skeletonPaths = (schema) => {
  const skeleton = buildSkeleton(schema);
  const out = [];

  const render = (segments) =>
    `$${segments.map((s) => (/^[A-Za-z_$][\w$]*$/.test(s) ? `.${s}` : `['${s}']`)).join('')}`;

  const walk = (node, segments) => {
    if (node !== null && typeof node === 'object' && !Array.isArray(node)) {
      const keys = Object.keys(node);
      if (keys.length === 0) return;
      keys.forEach((key) => walk(node[key], segments.concat(key)));
      return;
    }
    if (Array.isArray(node)) {
      if (node.length > 0) walk(node[0], segments.concat('0'));
      return;
    }
    if (segments.length > 0) out.push(render(segments));
  };

  walk(skeleton, []);
  return out;
};
